// ScorePenaltyBar.js — PRD-133 (WP4): theme-aware decomposition of the deterministic scanPenalty
// by scan source × severity. Recharts stacked BarChart, colors from useThemeColors().severity.*
// (no hardcoded hex per CLAUDE.md §6). Follows the RiskTrendChart.js pattern.
//
// IMPORTANT (the SEED's exact UX trap): this is NOT the risk score and NOT an additive total. Each
// segment is that severity's PENALTY CONTRIBUTION (finding count × the computeRawPenalty weight
// C25/H10/M3/L1) — i.e. "what's driving the score," not "score = Σ penalty." The displayed score
// stays Claude's holistic baselineRiskScore.

import React, { useMemo } from 'react';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip
} from 'recharts';
import { useThemeColors } from '../../hooks/useThemeColors';
import './ScorePenaltyBar.css';

// Mirrors api/shared/storage.js::computeRawPenalty (PRD A2). info carries 0 weight, so it
// contributes nothing to the penalty and is omitted from the stack.
const PENALTY_WEIGHTS = { critical: 25, high: 10, medium: 3, low: 1 };
const SEVERITY_ORDER = ['low', 'medium', 'high', 'critical']; // stack low→critical (critical on top)

// User-facing source labels (keys come from the API's findingsBySource buckets).
const SOURCE_LABELS = {
    'discovery': 'Discovery',
    'vuln-scan': 'Vuln Scan',
    'deep-security': 'Deep',
    'drift': 'Drift',
    'other': 'Other'
};

function PenaltyTooltip({ active, payload, label }) {
    if (!active || !payload || !payload.length) return null;
    const counts = payload[0]?.payload?._counts || {};
    const total = payload.reduce((sum, p) => sum + (p.value || 0), 0);
    return (
        <div className="score-penalty-tooltip">
            <div className="score-penalty-tooltip__source">{SOURCE_LABELS[label] || label}</div>
            {[...payload].reverse().map((p) => {
                const sev = p.dataKey;
                const count = counts[sev] || 0;
                if (!p.value) return null;
                return (
                    <div key={sev} className="score-penalty-tooltip__row">
                        <span className="score-penalty-tooltip__sev" style={{ color: p.fill }}>
                            {sev.charAt(0).toUpperCase() + sev.slice(1)}
                        </span>
                        <span>{count} × {PENALTY_WEIGHTS[sev]} = {p.value} pts</span>
                    </div>
                );
            })}
            <div className="score-penalty-tooltip__total">{total} penalty pts</div>
        </div>
    );
}

/**
 * @param {object} props
 * @param {Object<string,{critical:number,high:number,medium:number,low:number}>} props.findingsBySource
 *        The asset-score-breakdown `findingsBySource` map (post-WP1 correct bucketing).
 */
function ScorePenaltyBar({ findingsBySource }) {
    const colors = useThemeColors();

    const chartData = useMemo(() => {
        if (!findingsBySource) return [];
        return Object.entries(findingsBySource)
            .map(([source, counts]) => {
                const row = { source, _counts: {} };
                let rowTotal = 0;
                for (const sev of SEVERITY_ORDER) {
                    const n = counts?.[sev] || 0;
                    const pts = n * PENALTY_WEIGHTS[sev];
                    row[sev] = pts;
                    row._counts[sev] = n;
                    rowTotal += pts;
                }
                row._total = rowTotal;
                return row;
            })
            // Drop sources with zero penalty contribution (e.g. info-only / discovery noise)
            .filter((row) => row._total > 0)
            .sort((a, b) => b._total - a._total);
    }, [findingsBySource]);

    if (!chartData.length) {
        return (
            <div className="score-penalty-empty">
                No penalty-driving findings to break down.
            </div>
        );
    }

    const gridColor = colors.border?.dark || '#CBD5E1';
    const axisColor = colors.text?.muted || '#64748B';
    const sevColors = {
        critical: colors.severity?.critical || '#DC2626',
        high: colors.severity?.high || '#EA580C',
        medium: colors.severity?.medium || '#F59E0B',
        low: colors.severity?.low || '#16A34A'
    };

    return (
        <div
            className="score-penalty-bar"
            role="img"
            aria-label={`Penalty contribution by scan source and severity. ${chartData
                .map((r) => `${SOURCE_LABELS[r.source] || r.source}: ${r._total} points`)
                .join('; ')}.`}
        >
            <ResponsiveContainer width="100%" height={Math.max(120, chartData.length * 46 + 36)}>
                <BarChart
                    layout="vertical"
                    data={chartData}
                    margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                >
                    <CartesianGrid strokeDasharray="3 3" stroke={gridColor} horizontal={false} />
                    <XAxis
                        type="number"
                        stroke={axisColor}
                        tick={{ fontSize: 11, fill: axisColor }}
                        tickLine={false}
                        axisLine={{ stroke: gridColor }}
                    />
                    <YAxis
                        type="category"
                        dataKey="source"
                        tickFormatter={(s) => SOURCE_LABELS[s] || s}
                        stroke={axisColor}
                        tick={{ fontSize: 11, fill: axisColor }}
                        tickLine={false}
                        axisLine={{ stroke: gridColor }}
                        width={84}
                    />
                    <Tooltip content={<PenaltyTooltip />} cursor={{ fill: gridColor, fillOpacity: 0.2 }} />
                    {SEVERITY_ORDER.map((sev, idx) => (
                        <Bar
                            key={sev}
                            dataKey={sev}
                            stackId="penalty"
                            fill={sevColors[sev]}
                            isAnimationActive={true}
                            animationDuration={420}
                            radius={idx === SEVERITY_ORDER.length - 1 ? [0, 3, 3, 0] : 0}
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>
            <p className="score-penalty-bar__caption">
                What's driving the score (deterministic penalty contribution, critical = 25× a low).
                Your score is Claude's holistic assessment, <strong>not a sum of these</strong>.
            </p>
        </div>
    );
}

export default ScorePenaltyBar;
